import React, { useEffect, useState } from "react"
import { Button, ButtonDropdown, Card, CardBody, Col, DropdownItem, DropdownMenu, DropdownToggle } from "reactstrap"
import { Email } from 'models/Email';
import { Link, useNavigate } from "react-router-dom";
import { deleteEmail, readEmail, setEmailCustomer, starEmail } from "store/actions/Emails";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import { RootState } from "store/store";
import { GetRouterQuery } from "components/Utils/GetRouterQuery";
import { EmailState } from 'store/reducers/Emails';
import prettyBytes from 'pretty-bytes';
import { fetchEmailAttachments, getEmailAttachmentContent } from "services/Api";
import { MailAttachment } from "models/Document";
import LongClickButton from "../../components/LongClickButton";
import { Account } from "../../models/Account";
import { CustomerState } from "../../store/reducers/Customers";
import { getCustomers } from "../../store/actions/Customers";
import { UuidNil } from "../../common/uuid";
import { useOfficeTranslation } from "i18n/OfficeTranslation";

import MarkAsSpamButton from "./MarkAsSpamButton";
import EmailAttachmentViewer, {EmailAttachmentPreview} from "./EmailAttachmentViewer";

interface Props {
    id: string;
    emailState: EmailState;
    customers: CustomerState;
    withoutReplies?: boolean;
    withoutFollowUps?: boolean;
}

interface Actions {
    getCustomers: () => void;
    readEmail: (email: Email) => void;
    deleteEmail: (email: Email) => Promise<any>;
    starEmail: (e: Email, b: any) => void;
    setEmailCustomer: (e: Email, customer: string) => Promise<Email>;
}

export const EmailCardBase: React.FC<Props & Actions> = ({
    setEmailCustomer,
    getCustomers,
    deleteEmail,
    emailState,
    customers,
    id,
    starEmail,
    readEmail,
    withoutReplies,
    withoutFollowUps
}) => {
    const { intlLocale, t } = useOfficeTranslation();
    const [showQuote, setShowQuote] = useState(false)
    const [attachments, setAttachments] = useState<MailAttachment[]>([])
    const [attachmentSources, setAttachmentSources] = useState<Record<string, string>>({})
    const [attachmentPreview, setAttachmentPreview] = useState<EmailAttachmentPreview | null>(null);
    const [dropNew, setDropNew] = useState(false);
    const [suggestedCustomers, setSuggestedCustomers] = useState<Account[]>([])

    const navigate = useNavigate();

    useEffect(() => {
        let cancelled = false;
        setAttachmentPreview(null);
        setAttachments([]);
        setAttachmentSources({});
        const objectUrls: string[] = [];
        fetchEmailAttachments(id).then(async values => {
            const nextAttachments = values ?? [];
            const sources = await Promise.all(nextAttachments.map(async attachment => {
                if (attachment.legacy_content) return [attachment.id, attachment.legacy_content] as const;
                try {
                    const content = await getEmailAttachmentContent(id, attachment.id);
                    const source = URL.createObjectURL(content);
                    objectUrls.push(source);
                    return [attachment.id, source] as const;
                } catch {
                    return [attachment.id, ""] as const;
                }
            }));
            if (cancelled) {
                objectUrls.forEach(source => URL.revokeObjectURL(source));
                return;
            }
            setAttachments(nextAttachments);
            setAttachmentSources(Object.fromEntries(sources));
        }).catch(() => {
            if (!cancelled) {
                setAttachments([]);
                setAttachmentSources({});
            }
        });
        return () => {
            cancelled = true;
            objectUrls.forEach(source => URL.revokeObjectURL(source));
        };
    }, [id])

    const email = emailState.getEmailById(id);

    useEffect(() => {
        if (email === null) {
            return
        }
        if (email.customer === UuidNil && email.direction === "in" && !customers.isFetched()) {
            getCustomers()
        }

        if (customers.isFetched()) {
            const suggested = customers.findByEmail(email.sender)
            if (suggested.length > 0) {
                setSuggestedCustomers(suggested);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customers, email])


    if (email === null) {
        return <></>;
    }

    const viewingId = GetRouterQuery<{ id: string }>().id

    const star = () => {
        if (email.starred) {
            starEmail(email, { "starred": false })
        } else {
            starEmail(email, { "starred": true })
        }
    }


    let cleanBody = ""
    let rawBody = email.body
        .replace(/(\r\n|\n|\r)/gm, "")
        .replaceAll(/<img(.*?)>/img, "")
        .replaceAll(/<style(.*?)>(.*?)<\/style>/img, "")
        .replaceAll(/style="(.*?)"/img, "")
    if (!showQuote) {
        cleanBody = rawBody.replaceAll(/<blockquote (.*?)>(.*?)<\/blockquote>/img, "")
    } else {
        cleanBody = rawBody
    }


    const followUpEmails = emailState.getEmails().filter(email => email.reply_to === id)
    const failureReason = email.GetFailureReason()
    const deliveryStatus = email.GetStatusLabel()
    const pendingDeliveryLabel = deliveryStatus === "QUEUED" ? t("email.emailCard.queued")
        : deliveryStatus === "RETRYING" ? t("email.emailCard.retrying") : null
    const deliveryErrorLabel = failureReason !== null
        ? `${t("email.emailCard.deliveryFailed")} ${t("email.emailCard.failureReason", {reason: failureReason})}`
        : t("email.emailCard.deliveryFailed")
    return (
        <>
            {!withoutFollowUps && followUpEmails.length > 0 &&
                <>
                    {followUpEmails
                        .sort((a, b) => (a.date < b.date) ? 1 : (a.date > b.date) ? -1 : 0).map(followUp => {
                            return <EmailCard key={followUp.id} id={followUp.id} withoutReplies={true} />
                        })}
                </>
            }

            <Card className={`email-detail${viewingId === id ? " email-detail--current" : ""}`}>
                <CardBody className="email-detail__layout">
                    <header className="email-detail__heading">
                        <div className="email-detail__subject-row">
                            <h4 className="email-detail__subject">
                                {viewingId === id ? email.subject :
                                    <Link to={`/conversation/${email.id}`}>{email.subject}</Link>}
                            </h4>
                            <button type="button" className="email-detail__star" onClick={star}
                                aria-pressed={email.starred} aria-label={t("email.emailSideBar.starred")}
                                title={t("email.emailSideBar.starred")}>
                                <i aria-hidden="true" className={email.starred ? "bx bxs-star text-warning" : "bx bx-star"} />
                            </button>
                        </div>
                        <div className="email-detail__metadata">
                            <time dateTime={email.GetDate().toISOString()}>
                                {email.GetDate().toLocaleDateString(intlLocale, {day: "numeric", month: "short", year: "numeric"})}
                                {" · "}{email.GetDate().toLocaleTimeString(intlLocale, {hour: "2-digit", minute: "2-digit"})}
                            </time>
                            {email.IsRead() ?
                                <span className="email-detail__read-state" title={new Date(email.reader[0].date).toString()}>
                                    {t("email.emailCard.read")}
                                </span> : email.direction === "in" ?
                                <button type="button" className="email-detail__read-state" onClick={() => readEmail(email)}
                                    title={t("email.emailCard.markAsRead")} aria-label={t("email.emailCard.markAsRead")}>
                                    {t("email.emailCard.unread")}
                                </button> :
                                <span className="email-detail__read-state">{t("email.emailCard.unread")}</span>
                            }
                            {email.direction === "out" && pendingDeliveryLabel !== null &&
                                <span role="img" aria-label={pendingDeliveryLabel} title={pendingDeliveryLabel}>
                                    <i className="mdi mdi-timer-sand" aria-hidden="true" />
                                </span>}
                            {email.direction === "out" && email.HasFailed() &&
                                <span role="img" className="text-danger" aria-label={deliveryErrorLabel} title={deliveryErrorLabel}>
                                    <i className="mdi mdi-alert-circle-outline" aria-hidden="true" />
                                </span>}
                            {email.reference.includes("invoice") &&
                                <Link to={"/invoice/" + email.reference.replace("invoice_", "")}>{t("common.invoice")}</Link>}
                            {email.reference.includes("ticket") && <span>{t("email.emailCard.ticket")}</span>}
                        </div>
                    </header>

                    <div className="email-detail__sender">
                        <div className="email-detail__avatar">
                            {email.customer !== UuidNil ?
                                <Link to={`/customers/${email.customer}`} className="email-detail__initial" aria-label={email.sender_name}>
                                    {email.sender_name.charAt(0)}
                                </Link> :
                                <span className="email-detail__initial">{email.sender_name.charAt(0)}</span>}
                            <span className={`email-detail__channel ${email.type === "whatsapp" ? "bg-success" : "bg-purple"}`}
                                role="img" aria-label={email.type === "whatsapp" ? t("email.channel.whatsapp") : t("common.email")}
                                title={email.type === "whatsapp" ? t("email.channel.whatsapp") : t("common.email")}>
                                <i aria-hidden="true" className={email.type === "whatsapp" ? "bx bxl-whatsapp" : "bx bx-envelope"} />
                            </span>
                        </div>
                        <div className="email-detail__sender-details">
                            <div className="email-detail__identity">
                                {email.direction === "in" ? <>
                                    <strong>{email.sender_name}</strong>
                                    <span className="text-muted">{email.sender}</span>
                                </> : <strong>{email.recipient}</strong>}
                            </div>
                            <div className="email-detail__recipient text-muted">
                                {email.direction === "in" ? <>{t("common.to")} {email.recipient}</> :
                                    <>{t("common.from")} {email.sender_name} &lt;{email.sender}&gt;</>}
                            </div>
                            {email.carbon_copy && <div className="email-detail__recipient text-muted">
                                {t("email.emailCard.cc")} {email.carbon_copy}
                            </div>}
                            {suggestedCustomers.length > 0 &&
                                <ButtonDropdown isOpen={dropNew} toggle={() => setDropNew(!dropNew)} className="mt-2">
                                    <DropdownToggle caret color="light" size="sm">
                                        <i className="mdi mdi-link" aria-hidden="true" /> {t("common.link")}
                                    </DropdownToggle>
                                    <DropdownMenu>
                                        {suggestedCustomers.map(customer => <DropdownItem key={customer.id} onClick={() => {
                                            setEmailCustomer(email, customer.id).then(() => setSuggestedCustomers([]));
                                        }}>{customer.legal_name}</DropdownItem>)}
                                    </DropdownMenu>
                                </ButtonDropdown>}
                        </div>
                    </div>

                    <div className="email-detail__content">
                        {email.direction === "out" && email.HasFailed() &&
                            <div className="alert alert-danger" role="alert">
                                <strong>{t("email.emailCard.deliveryFailed")}</strong>{" "}
                                {failureReason !== null ?
                                    t("email.emailCard.failureReason", {reason: failureReason}) :
                                    t("email.emailCard.failureReasonUnavailable")
                                }
                            </div>
                        }

                        <div className="email_body" dangerouslySetInnerHTML={{ __html: cleanBody }} />

                        {(cleanBody !== rawBody) &&
                            <>
                                {showQuote ?
                                    <div className="text-muted" style={{ cursor: "pointer" }}
                                        onClick={() => setShowQuote(false)}>{t("email.emailCard.hideQuoted")}</div>
                                    :
                                    <div className="text-muted" style={{ cursor: "pointer" }}
                                        onClick={() => setShowQuote(true)}>{t("email.emailCard.showQuoted")}</div>
                                }
                            </>
                        }

                        <div className="dropzone-previews mt-3" id="file-previews">
                            {attachments.map((attachment, i) => {
                                const source = attachmentSources[attachment.id] || "";
                                const mimeType = attachment.content_type || attachment.legacy_content?.slice(5).split(";")[0] || "application/octet-stream";
                                const isPdf = mimeType === "application/pdf";
                                const preview = source && isPdf ?
                                    <i className="mdi mdi-file-pdf-box text-danger font-size-24" aria-hidden="true" />
                                    : source && mimeType.startsWith("image/") ?
                                    <img
                                        data-dz-thumbnail=""
                                        height="80"
                                        className="avatar-sm rounded bg-light"
                                        alt={attachment.name}
                                        src={source}
                                    />
                                    : source && mimeType.startsWith("video/") ?
                                        <video height="120" controls src={source} />
                                        : source && mimeType.startsWith("audio/") ?
                                            <audio controls src={source} />
                                            :
                                            <>
                                                <p className="mb-0">
                                                    {t("common.previewUnavailable")}
                                                </p>
                                            </>
                                return (
                                    <Col lg={3} key={attachment.id}>
                                        <Card
                                            className="mt-1 mb-0 shadow-none border dz-processing dz-image-preview dz-success dz-complete"
                                            key={i + "-file"}
                                        >
                                            <div className="p-2">
                                                {source && isPdf ?
                                                    <Button color="link" className="p-0 text-start text-break" onClick={() =>
                                                        setAttachmentPreview({name: attachment.name, source})}>
                                                        {attachment.name}{attachment.size !== undefined ? ` (${prettyBytes(attachment.size)})` : ""}
                                                    </Button>
                                                    : source ? <a target={"_blank"} rel="noreferrer" href={source}>
                                                        {attachment.name}{attachment.size !== undefined ? ` (${prettyBytes(attachment.size)})` : ""}
                                                    </a>
                                                    : <span>{attachment.name}</span>
                                                }<br />
                                                {
                                                    preview
                                                }
                                            </div>
                                        </Card>
                                    </Col>
                                );
                            })}
                        </div>
                    </div>
                    <div className="email-detail__actions" role="group" aria-label={t("email.emailCard.actions")}>
                        {email.direction === "in" &&
                            <Link to={"/conversations/compose?reply=" + email.id}
                                className="btn email-detail__action email-detail__action--reply"
                                title={t("email.emailCard.reply")} aria-label={t("email.emailCard.reply")}>
                                <i className="mdi mdi-reply" aria-hidden="true" />
                            </Link>}
                        <MarkAsSpamButton emails={[email]} iconOnly onMoved={() => navigate("/conversations/inbox")} />
                        <LongClickButton color="light" className="email-detail__action email-detail__action--delete"
                            title={t("email.emailCard.holdToDelete")} aria-label={t("common.delete")}
                            onClick={() => deleteEmail(email).then(() =>
                                navigate(email.direction === "in" ? "/conversations/inbox" : "/conversations/sent"))}>
                            <i className="mdi mdi-trash-can-outline" aria-hidden="true" />
                        </LongClickButton>
                    </div>
                </CardBody>
            </Card>
            <EmailAttachmentViewer preview={attachmentPreview} onClose={() => setAttachmentPreview(null)} />
            {(!withoutReplies && email.reply_to !== UuidNil) &&
                <EmailCard id={email.reply_to} withoutFollowUps={true} />
            }
        </>
    )
}

const mapStateToProps = (state: RootState) => {
    return {
        customers: state.Customers,
        emailState: state.Emails,
    };
};

const mapDispatchToProps = (dispatch: any) => {
    return bindActionCreators({ setEmailCustomer, getCustomers, deleteEmail, readEmail, starEmail }, dispatch);
};


const EmailCard = connect(mapStateToProps, mapDispatchToProps)(EmailCardBase);

export default EmailCard;
